import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import Logo from "@/components/Logo";
import { useAuth } from "@/contexts/AuthContext";

const ROLES = [
  { v: "analyste", l: "Analyste / Expert" },
  { v: "parlementaire", l: "Parlementaire" },
  { v: "chercheur", l: "Chercheur / Universitaire" },
  { v: "journaliste", l: "Journaliste" },
  { v: "citoyen", l: "Citoyen engagé" },
];

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", password: "", organization: "", country: "", role: "analyste",
  });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error("Mot de passe : minimum 6 caractères");
      return;
    }
    setLoading(true);
    try {
      await register(form);
      toast.success("Compte créé");
      nav("/app/projects");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur d'inscription");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="flex items-center justify-center p-6 lg:p-12 order-2 lg:order-1">
        <form onSubmit={onSubmit} className="w-full max-w-md" data-testid="register-form">
          <div className="lg:hidden mb-8"><Logo /></div>
          <h1 className="font-serif font-bold text-3xl">Créer un compte</h1>
          <p className="mt-2 text-muted-foreground text-sm">
            Rejoignez l'espace d'analyse souveraine de la dette publique.
          </p>
          <div className="mt-8 grid grid-cols-1 gap-4">
            <div>
              <Label htmlFor="name">Nom complet</Label>
              <Input id="name" data-testid="register-name" value={form.name} onChange={set("name")} required className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="register-email" type="email" value={form.email} onChange={set("email")} required className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="password">Mot de passe (min. 6)</Label>
              <Input id="password" data-testid="register-password" type="password" value={form.password} onChange={set("password")} required className="mt-1.5" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="org">Organisation</Label>
                <Input id="org" data-testid="register-org" value={form.organization} onChange={set("organization")} className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="country">Pays</Label>
                <Input id="country" data-testid="register-country" value={form.country} onChange={set("country")} className="mt-1.5" />
              </div>
            </div>
            <div>
              <Label>Profil</Label>
              <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger className="mt-1.5" data-testid="register-role-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" disabled={loading} data-testid="register-submit-btn"
                    className="w-full h-11 bg-marine hover:bg-marine/90 text-white">
              {loading ? "Création…" : "Créer mon compte"}
            </Button>
          </div>
          <p className="mt-6 text-sm text-muted-foreground">
            Déjà inscrit ?{" "}
            <Link to="/login" data-testid="link-to-login" className="text-marine dark:text-gold font-semibold hover:underline">Se connecter</Link>
          </p>
        </form>
      </div>
      <div className="hidden lg:flex relative border-l border-border overflow-hidden order-1 lg:order-2">
        <div className="absolute inset-0 bg-marine" />
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="relative z-10 p-16 h-full flex flex-col justify-between text-white">
          <Logo />
          <div>
            <div className="label-mono text-[10px] text-gold">MÉTHODOLOGIE</div>
            <h2 className="mt-4 font-serif text-3xl leading-tight">
              Un cadre analytique inédit :<br />la dette mesurée en <em className="text-gold">vies humaines</em>.
            </h2>
            <p className="mt-6 text-sm text-white/70">
              D1, D2, D4, D5 · TAVD V5 · Hypothèque V6.<br />
              Théorie Nouvelle des Finances Publiques — A. ELY Mustapha.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
